import { useState } from 'react';
import { productInstructions, productCharacteristics, productReviews, productAnalogues } from '../../../mocks/product';

const color = '#ff2d78';

const InstructionTab = () => {
  const [openSection, setOpenSection] = useState<string | null>('indications');
  const sections = [
    { id: 'composition', label: 'Состав', icon: 'ri-test-tube-line', content: productInstructions.composition },
    { id: 'indications', label: 'Показания', icon: 'ri-stethoscope-line', content: null, list: productInstructions.indications },
    { id: 'contraindications', label: 'Противопоказания', icon: 'ri-forbid-line', content: null, list: productInstructions.contraindications, warn: true },
    { id: 'dosage', label: 'Дозировка и приём', icon: 'ri-capsule-line', content: productInstructions.dosage },
    { id: 'sideEffects', label: 'Побочные эффекты', icon: 'ri-alert-line', content: productInstructions.sideEffects },
    { id: 'storage', label: 'Условия хранения', icon: 'ri-archive-line', content: productInstructions.storage },
  ];

  return (
    <div className="space-y-2">
      {sections.map(s => (
        <div
          key={s.id}
          className="rounded-xl overflow-hidden"
          style={{ border: `1px solid ${openSection === s.id ? color + '40' : 'rgba(255,255,255,0.06)'}` }}
        >
          <button
            onClick={() => setOpenSection(openSection === s.id ? null : s.id)}
            className="w-full flex items-center justify-between px-5 py-4 cursor-pointer transition-colors"
            style={{ background: openSection === s.id ? `${color}08` : 'rgba(255,255,255,0.02)' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={s.icon} style={{ color: openSection === s.id ? color : 'rgba(255,255,255,0.4)', fontSize: '16px' }} />
              </div>
              <span className="font-orbitron font-bold text-sm text-white/80">{s.label}</span>
            </div>
            <i className={`ri-arrow-${openSection === s.id ? 'up' : 'down'}-s-line text-white/40`} />
          </button>

          {openSection === s.id && (
            <div className="px-5 pb-5 pt-1">
              {s.list ? (
                <ul className="space-y-2">
                  {s.list.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <div
                        className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
                        style={{ background: s.warn ? '#ff2d78' : color, boxShadow: `0 0 4px ${s.warn ? '#ff2d78' : color}` }}
                      />
                      <span className="font-mono text-sm text-white/60 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="font-mono text-sm text-white/60 leading-relaxed">{s.content}</p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const CharacteristicsTab = () => (
  <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
    {productCharacteristics.map((c, i) => (
      <div
        key={i}
        className="flex items-center px-5 py-3.5"
        style={{
          background: i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'rgba(255,255,255,0.03)',
          borderBottom: i < productCharacteristics.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
        }}
      >
        <span className="font-mono text-xs text-white/40 w-48 shrink-0">{c.label}</span>
        <span className="font-mono text-sm text-white/80">{c.value}</span>
      </div>
    ))}
  </div>
);

const ReviewsTab = () => {
  const [showForm, setShowForm] = useState(false);
  const [newRating, setNewRating] = useState(5);
  const [submitted, setSubmitted] = useState(false);
  const [liked, setLiked] = useState<number[]>([]);

  const avgRating = 4.9;
  const distribution = [{ stars: 5, count: 251 }, { stars: 4, count: 41 }, { stars: 3, count: 12 }, { stars: 2, count: 5 }, { stars: 1, count: 3 }];

  return (
    <div className="space-y-8">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div
          className="flex flex-col items-center justify-center p-8 rounded-2xl"
          style={{ background: `${color}08`, border: `1px solid ${color}20` }}
        >
          <span className="font-orbitron font-black text-7xl mb-2" style={{ color, textShadow: `0 0 30px ${color}` }}>{avgRating}</span>
          <div className="flex gap-1 mb-1">
            {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill" style={{ color: '#f0ff00' }} />)}
          </div>
          <span className="font-mono text-xs text-white/40">На основе 312 отзывов</span>
        </div>

        <div className="flex flex-col justify-center gap-2">
          {distribution.map(d => (
            <div key={d.stars} className="flex items-center gap-3">
              <div className="flex gap-0.5 shrink-0">
                {[1,2,3,4,5].map(s => <i key={s} className={`text-[10px] ${s <= d.stars ? 'ri-star-fill' : 'ri-star-line'}`} style={{ color: '#f0ff00' }} />)}
              </div>
              <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${(d.count / 312) * 100}%`,
                    background: color,
                    boxShadow: `0 0 8px ${color}`,
                  }}
                />
              </div>
              <span className="font-mono text-xs text-white/40 w-8 text-right">{d.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Write review button */}
      <button
        onClick={() => setShowForm(f => !f)}
        className="whitespace-nowrap flex items-center gap-2 px-6 py-3 rounded-xl font-mono text-sm cursor-pointer transition-all duration-300"
        style={{ border: `1px solid ${color}40`, color }}
      >
        <i className="ri-edit-line" />
        НАПИСАТЬ ОТЗЫВ
      </button>

      {/* Review form */}
      {showForm && !submitted && (
        <div className="p-6 rounded-2xl space-y-4" style={{ border: `1px solid ${color}30`, background: `${color}05` }}>
          <h4 className="font-orbitron font-bold text-sm text-white">Ваш отзыв</h4>
          <div>
            <label className="font-mono text-xs text-white/40 mb-2 block">ОЦЕНКА</label>
            <div className="flex gap-2">
              {[1,2,3,4,5].map(s => (
                <button key={s} onClick={() => setNewRating(s)} className="cursor-pointer transition-transform hover:scale-110">
                  <i className={`text-2xl ${s <= newRating ? 'ri-star-fill' : 'ri-star-line'}`} style={{ color: '#f0ff00' }} />
                </button>
              ))}
            </div>
          </div>
          <textarea
            placeholder="Поделитесь впечатлениями..."
            maxLength={500}
            className="w-full px-4 py-3 rounded-xl font-mono text-sm text-white/80 bg-transparent outline-none resize-none h-28"
            style={{ border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.02)' }}
          />
          <button
            onClick={() => setSubmitted(true)}
            className="whitespace-nowrap px-6 py-2.5 rounded-xl font-orbitron font-bold text-xs cursor-pointer transition-all duration-300"
            style={{ background: color, color: '#050508', boxShadow: `0 0 15px ${color}60` }}
          >
            ОТПРАВИТЬ
          </button>
        </div>
      )}
      {submitted && (
        <div className="p-4 rounded-xl flex items-center gap-3" style={{ border: '1px solid rgba(57,255,20,0.3)', background: 'rgba(57,255,20,0.05)' }}>
          <i className="ri-check-line text-lg" style={{ color: '#39ff14' }} />
          <span className="font-mono text-sm" style={{ color: '#39ff14' }}>Отзыв отправлен на модерацию. Спасибо!</span>
        </div>
      )}

      {/* Reviews list */}
      <div className="space-y-4">
        {productReviews.map(review => (
          <div
            key={review.id}
            className="p-5 rounded-2xl"
            style={{ border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.015)' }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center font-orbitron font-bold text-xs"
                  style={{ background: `${color}20`, border: `1px solid ${color}40`, color }}
                >
                  {review.author.charAt(0)}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-white/80">{review.author}</span>
                    {review.verified && (
                      <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(0,245,255,0.1)', color: '#00f5ff', border: '1px solid rgba(0,245,255,0.3)' }}>
                        <i className="ri-check-line mr-0.5" />
                        Покупатель
                      </span>
                    )}
                  </div>
                  <span className="font-mono text-[10px] text-white/30">{review.date} · {review.form}</span>
                </div>
              </div>
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(s => (
                  <i key={s} className={`text-xs ${s <= review.rating ? 'ri-star-fill' : 'ri-star-line'}`} style={{ color: '#f0ff00' }} />
                ))}
              </div>
            </div>

            <p className="font-mono text-sm text-white/60 leading-relaxed mb-3">{review.text}</p>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setLiked(l => l.includes(review.id) ? l.filter(x => x !== review.id) : [...l, review.id])}
                className="flex items-center gap-1.5 cursor-pointer transition-colors"
                style={{ color: liked.includes(review.id) ? color : 'rgba(255,255,255,0.3)' }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={liked.includes(review.id) ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} style={{ fontSize: '14px' }} />
                </div>
                <span className="font-mono text-xs">{review.likes + (liked.includes(review.id) ? 1 : 0)}</span>
              </button>
              <span className="font-mono text-xs text-white/20">Полезно?</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AnaloguesTab = () => {
  const [cart, setCart] = useState<number[]>([]);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {productAnalogues.map(product => (
        <div
          key={product.id}
          className="rounded-2xl overflow-hidden group"
          style={{ border: `1px solid ${product.color}20`, background: 'rgba(255,255,255,0.02)' }}
        >
          <div
            className="h-40 flex items-center justify-center relative"
            style={{ background: `linear-gradient(135deg, #0a0a14 0%, ${product.color}08 100%)` }}
          >
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-12 rounded-full opacity-20 group-hover:opacity-40 transition-opacity" style={{ background: product.color, filter: 'blur(15px)' }} />
            <img src={product.image} alt={product.name} className="w-28 h-28 object-contain relative z-10 group-hover:scale-105 transition-transform duration-300" style={{ filter: `drop-shadow(0 8px 16px ${product.color}50)` }} />
          </div>
          <div className="p-4">
            <div className="font-mono text-[10px] px-2 py-0.5 rounded-full inline-block mb-2" style={{ background: `${product.color}15`, color: product.color, border: `1px solid ${product.color}30` }}>
              {product.badge}
            </div>
            <div className="font-orbitron font-bold text-sm text-white mb-1">{product.name}</div>
            <div className="flex items-center gap-1 mb-3">
              <i className="ri-star-fill text-[10px]" style={{ color: '#f0ff00' }} />
              <span className="font-mono text-xs text-white/40">{product.rating} · {product.reviews} отзывов</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-orbitron font-bold text-base" style={{ color: product.color }}>{product.price.toLocaleString()}₽</div>
                <div className="font-mono text-[10px] text-white/25 line-through">{product.oldPrice.toLocaleString()}₽</div>
              </div>
              <button
                onClick={() => setCart(c => c.includes(product.id) ? c : [...c, product.id])}
                className="w-9 h-9 flex items-center justify-center rounded-xl cursor-pointer transition-all duration-300"
                style={cart.includes(product.id)
                  ? { background: product.color, boxShadow: `0 0 10px ${product.color}` }
                  : { border: `1px solid ${product.color}`, color: product.color }
                }
              >
                <i className={cart.includes(product.id) ? 'ri-check-line' : 'ri-add-line'} style={{ color: cart.includes(product.id) ? '#050508' : product.color, fontSize: '16px' }} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const TABS = [
  { id: 'instructions', label: 'Инструкция', icon: 'ri-file-list-3-line' },
  { id: 'characteristics', label: 'Характеристики', icon: 'ri-list-check-2' },
  { id: 'reviews', label: 'Отзывы (312)', icon: 'ri-chat-3-line' },
  { id: 'analogues', label: 'Аналоги', icon: 'ri-exchange-line' },
];

const ProductTabs = () => {
  const [activeTab, setActiveTab] = useState('instructions');

  return (
    <section className="py-16 px-4 md:px-8 lg:px-12" style={{ background: '#050508' }}>
      <div className="max-w-7xl mx-auto">

        {/* Tab bar */}
        <div
          className="flex overflow-x-auto gap-1 p-1 rounded-2xl mb-8"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
        >
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="whitespace-nowrap flex items-center gap-2 px-5 py-3 rounded-xl font-mono text-xs tracking-wider cursor-pointer transition-all duration-300 shrink-0"
              style={activeTab === tab.id
                ? { background: color, color: '#050508', boxShadow: `0 0 15px ${color}60` }
                : { color: 'rgba(255,255,255,0.4)' }
              }
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={tab.icon} style={{ fontSize: '14px' }} />
              </div>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'instructions' && <InstructionTab />}
        {activeTab === 'characteristics' && <CharacteristicsTab />}
        {activeTab === 'reviews' && <ReviewsTab />}
        {activeTab === 'analogues' && <AnaloguesTab />}
      </div>
    </section>
  );
};

export default ProductTabs;
